import {useAccessibilityState} from '_context'
import {Breakpoint} from '_uiComponents'
import {HEADER_SIZE_COLLAPSED_WITHOUT_BANNER, HEADER_SIZE_NOT_COLLAPSED_WITHOUT_BANNER, addAccessibilityBannerHeight} from '_utils'

/**
 * @name useHeaderSizeWithoutBanner
 *
 * @hook
 *
 * @description
 * Custom hook that calculates the height of the header without the banner.
 *
 * @remarks
 * This size does not include the banner height for cases like the sidebar.
 *
 * @example
 * ```tsx
 * const headerSize = useHeaderSizeWithoutBanner();
 * const styles = useMultiStyleConfig('Sample', {headerSize}) as Record<string, any>
 * ```
 *
 * @example with `useScrollAtTop` custom hook usage
 * ```tsx
 * const isAtTop = useScrollAtTop()
 * const headerSize = useHeaderSizeWithoutBanner(!isAtTop)
 * const styles = useMultiStyleConfig('Sample', {headerSize}) as Record<string, any>
 * ```
 *
 * @param {boolean | undefined} isCollapsed - Indicates if the user has scrolled down from the top and the header has collapsed.
 *
 * @returns {Record<Breakpoint, number>} headerSize - Header size in number values for each available breakpoint.
 */
const useHeaderSizeWithoutBanner = (isCollapsed?: boolean): Record<Breakpoint, number> => {
    const {isAccessibilityBannerEnabled} = useAccessibilityState()
    let headerSize = HEADER_SIZE_COLLAPSED_WITHOUT_BANNER
    if (!isCollapsed) {
        headerSize = HEADER_SIZE_NOT_COLLAPSED_WITHOUT_BANNER
        if (isAccessibilityBannerEnabled) {
            // supporting pages without headerProvider and isAtTop information
            headerSize = addAccessibilityBannerHeight(headerSize)
        }
    }
    return headerSize
}

export {useHeaderSizeWithoutBanner}
